import { ServiceSlugs } from '@ply-io/ply-front-shared-utilities';

import { MODULE_NAME } from './const';
import {
	DELETE_TEMP_BLOCK,
	UPDATE_TEMP_BLOCK_PROPERTY,
	NODE_ID_TO_MOVE_LOCATION,
	MARK_MOVE_NODE_STATE,
	SET_MOVE_NODE_LOADING,
	SET_TEMP_BLOCK,
	SET_IS_END_RUN_LOADING,
	SET_SHORTCUT_COMMAND,
} from './mutations';
import state from './state';
import StoreModuleService from '../StoreModuleService';
import { createStep, handleGraphAffectedChanges } from '../nodes/actions';
import { closeSideBar, setEditNodeClick } from '../selectedNode/actions';
import { editNode as editNodeGetter, useNodeOutgoingEdges } from '../selectedNode/getters';
import { editedWorkflowVersion, rootNode, selectedWorkflowId } from '../selectedWorkflow/getters';
import api from '@/api';
import { NodeTypes } from '@/components/FlowTree/flow-tree-service.const';
import { getServiceBySlug } from '@/store/modules/apps/getters';
import { TEMP_NODE_ID_KEY_PREFIX } from '@/utils/workflow-node.const';

const buildTreeBlockActionObject = (action) => StoreModuleService.buildActionObject(MODULE_NAME, action);

// actions declarations
export const removeTempBlock = buildTreeBlockActionObject('removeTempBlock');
export const createNewNodeAfterCurrent = buildTreeBlockActionObject('createNewNodeAfterCurrent');
export const updateTempBlockProperty = buildTreeBlockActionObject('updateTempBlockProperty');
export const moveStepLocationInTree = buildTreeBlockActionObject('moveStepLocationInTree');
export const markMoveStepLocationInTreeActive = buildTreeBlockActionObject('markMoveStepLocationInTreeActive');
export const setMoveStepLocationInTreeInactive = buildTreeBlockActionObject('setMoveInactive');
export const setEdgeToCreateNodeIn = buildTreeBlockActionObject('setEdgeToCreateNodeIn');
export const setShortcutCommand = buildTreeBlockActionObject('setShortcutCommand');
export const addEndRunBlock = buildTreeBlockActionObject('addEndRunBlock');

const actions = {
	...StoreModuleService.createDefaultActions(),
	async [removeTempBlock.action]({ commit, dispatch, rootGetters }) {
		commit(SET_TEMP_BLOCK, null);
		commit(SET_SHORTCUT_COMMAND, null);
		for (const [tempBlockBranchNodeId, tempBlock] of Object.entries(state.tempBlocks)) {
			if (rootGetters[editNodeGetter.fullPath]?.id === tempBlock.id) {
				// if current edited block is the temp block - close side bar
				await dispatch(closeSideBar.fullPath, {}, { root: true });
			}
			commit(DELETE_TEMP_BLOCK, { key: tempBlockBranchNodeId });
		}
	},
	async [setEdgeToCreateNodeIn.action]({ commit, dispatch, rootGetters }, { edgeId, shortcutCommand }) {
		const tempId = `${TEMP_NODE_ID_KEY_PREFIX}${new Date().getUTCMilliseconds()}`;
		const newTempBlock = {
			id: tempId,
			temp: true,
			action: null,
			service: null,
			edge: edgeId,
			node_title: '',
			params_map: {},
			offspring_nodes: [],
			uuid: `uuid-${tempId}`,
			test_result_status: 'unknown',
			root_node: rootGetters[rootNode.fullPath].id,
			workflow: rootGetters[selectedWorkflowId.fullPath],
			workflow_version: rootGetters[editedWorkflowVersion.fullPath].id,
			credentials_configuration: { credentials: null, credentials_type: 'embedded' },
		};

		commit(SET_TEMP_BLOCK, newTempBlock);
		commit(SET_SHORTCUT_COMMAND, shortcutCommand);
		dispatch(setEditNodeClick.fullPath, newTempBlock, { root: true });
	},
	[setShortcutCommand.action]({ commit }, { shortcutCommand }) {
		commit(SET_SHORTCUT_COMMAND, shortcutCommand);
	},

	async [moveStepLocationInTree.action]({ commit, dispatch, rootGetters }, { edgeId }) {
		const moveNodeActive = state.moveNodeActive;
		const nodeIdToMove = state.nodeIdToMove;

		await dispatch(removeTempBlock.action); //make sure we don't have any other temp nodes so we don't complicate the search for the node destination location

		if (moveNodeActive && nodeIdToMove) {
			await dispatch(setMoveStepLocationInTreeInactive.action); // we prevent user from moving the node again before the previous move is done
			commit(SET_MOVE_NODE_LOADING, true);
			try {
				const changes = await api.moveStep(rootGetters[selectedWorkflowId.fullPath], nodeIdToMove, Number(edgeId));
				await dispatch(handleGraphAffectedChanges.fullPath, { changes }, { root: true });
			} finally {
				commit(SET_MOVE_NODE_LOADING, false);
			}
		}
	},
	[markMoveStepLocationInTreeActive.action]({ commit }, payload) {
		const { nodeId } = payload;
		if (nodeId) {
			commit(NODE_ID_TO_MOVE_LOCATION, nodeId);
			commit(MARK_MOVE_NODE_STATE, true);
		}
	},
	[setMoveStepLocationInTreeInactive.action]({ commit }) {
		commit(NODE_ID_TO_MOVE_LOCATION, null);
		commit(MARK_MOVE_NODE_STATE, false);
	},
	[createNewNodeAfterCurrent.action]({ dispatch, rootGetters }) {
		// this action is called when the user clicks on the "Add" button in the block editor test result
		const nodeOutgoingEdges = rootGetters[useNodeOutgoingEdges.fullPath];
		dispatch(setEdgeToCreateNodeIn.action, {
			edge: nodeOutgoingEdges[0] || { id: `${rootGetters[editNodeGetter.fullPath].id}=>${NodeTypes.EndRun}` },
		});
	},
	[updateTempBlockProperty.action]({ commit }, payload) {
		// const { id, key, value } = payload;
		commit(UPDATE_TEMP_BLOCK_PROPERTY, payload);
	},
	async [addEndRunBlock.action]({ dispatch, rootGetters, commit }) {
		try {
			const endRunServiceId = rootGetters[getServiceBySlug.fullPath](ServiceSlugs.EndRun).id;
			const node = { edge: null, service: endRunServiceId, action: null };
			commit(SET_IS_END_RUN_LOADING, true);
			const changes = await dispatch(createStep.fullPath, { node }, { root: true });
			const newEndRunStep = changes?.nodes?.created?.find((node) => node?.service === endRunServiceId);
			dispatch(setEditNodeClick.fullPath, newEndRunStep, { root: true });
		} catch (e) {
			throw ('Error occurred while adding end run block, please try again later.', e);
		} finally {
			commit(SET_IS_END_RUN_LOADING, false);
		}
	},
};

export default actions;
